import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Globe } from "lucide-react";
import AuthForm from "./AuthForm";
import HeaderNavLinks from "./header/HeaderNavLinks";
import HeaderDesktopUserMenu from "./header/HeaderDesktopUserMenu";
import HeaderMobileSheet from "./header/HeaderMobileSheet";
import useHeaderNotifications from "./header/useHeaderNotifications";

const LOW_BALANCE_THRESHOLD = 15;

/**
 * Header — top app bar, visible on every route.
 *
 * Responsibilities (delegated to child components / hooks):
 *   - logo + nav links               → HeaderNavLinks
 *   - desktop right cluster          → HeaderDesktopUserMenu
 *   - mobile hamburger + sheet       → HeaderMobileSheet
 *   - notification polling           → useHeaderNotifications
 *
 * This component itself owns:
 *   - cross-cutting window event listeners (`open-auth`,
 *     `credits-deducted`, `globaleyes:close-overlays`)
 *   - the low-balance toast (fires once per cross-down through 15 cr)
 *   - the shared auth dialog (opened by both desktop & mobile children)
 */
const Header = () => {
  const { user, logout, token } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [creditsBlink, setCreditsBlink] = useState(false);
  const { newFollowers, inboxUnread, expiredAlertsUnread } = useHeaderNotifications(user, token);

  // Listen for credits deduction to trigger blink animation on the pill.
  useEffect(() => {
    const handleCreditsDeducted = () => {
      setCreditsBlink(true);
      setTimeout(() => setCreditsBlink(false), 600);
    };
    window.addEventListener("credits-deducted", handleCreditsDeducted);
    return () => window.removeEventListener("credits-deducted", handleCreditsDeducted);
  }, []);

  // Global hook: any component anywhere can fire `window.dispatchEvent(
  // new CustomEvent('open-auth', {detail: {register: true}}))` to pop the
  // signup/login dialog. Used by the floating Record/Commission FAB so
  // logged-out viewers get a clear sign-up path instead of a dead click.
  useEffect(() => {
    const onOpenAuth = (e) => {
      const wantsRegister = !!(e && e.detail && e.detail.register);
      setIsLogin(!wantsRegister);
      setShowAuth(true);
    };
    window.addEventListener("open-auth", onOpenAuth);
    return () => window.removeEventListener("open-auth", onOpenAuth);
  }, []);

  // One-time low-balance toast — fires when credits cross below the
  // threshold. Re-arms once balance recovers so users get nudged again
  // on the next dip but never spammed continuously while low.
  const prevCreditsRef = React.useRef(null);
  const lowToastFiredRef = React.useRef(false);
  useEffect(() => {
    if (!user) {
      prevCreditsRef.current = null;
      lowToastFiredRef.current = false;
      return;
    }
    const credits = user.credits;
    const prev = prevCreditsRef.current;
    const justCrossedDown =
      (prev === null || prev >= LOW_BALANCE_THRESHOLD) && credits < LOW_BALANCE_THRESHOLD;
    if (justCrossedDown && !lowToastFiredRef.current) {
      toast.warning(
        `Heads up — balance is low (${credits.toFixed(2)}). Tap the credits pill to top up.`,
        { duration: 6000, id: "low-balance-warning" },
      );
      lowToastFiredRef.current = true;
    }
    if (credits >= LOW_BALANCE_THRESHOLD) {
      lowToastFiredRef.current = false;
    }
    prevCreditsRef.current = credits;
  }, [user]);

  const openAuthDialog = () => setShowAuth(true);

  return (
    <header
      className="w-full border-b bg-black/95 backdrop-blur supports-[backdrop-filter]:bg-black/80"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 999,
        // iOS notch / Dynamic Island clearance. Without this, the logo
        // sits behind the status bar at scroll-top until iOS rubber-bands.
        paddingTop: "env(safe-area-inset-top, 0px)",
        // translate3d forces own compositor layer so the header
        // cannot be demoted by ancestor transforms.
        transform: "translate3d(0, 0, 0)",
        WebkitTransform: "translate3d(0, 0, 0)",
      }}
      data-testid="site-header"
    >
      <div className="w-full relative flex xl:grid xl:grid-cols-[1fr_auto_1fr] xl:gap-6 h-14 sm:h-16 items-center px-4">
        {/* Left: Desktop Navigation — column 1 of the xl grid. min-w-0
            + overflow-hidden keeps a long localised nav from spilling
            into the centered logo (the "Leaderboard bleeding into
            GlobalEyes.TV" bug). */}
        <nav className="hidden xl:flex items-center gap-4 min-w-0 overflow-hidden">
          <HeaderNavLinks user={user} />
        </nav>

        {/* Mobile: balance pill moved into hamburger menu beside Dashboard
            link to free up logo space on small screens. */}
        <div className="flex xl:hidden items-center" />

        {/* Center: Logo. On mobile we keep the absolute-overlay
            centering (so the logo stays visually anchored regardless
            of right-side hamburger width); on xl+ we let it occupy
            the natural middle column of the 3-col grid, which
            guarantees a protected zone between left nav + right user
            menu. iOS WKWebView tap-highlight bug — the
            `-webkit-tap-highlight-color` engine ignores `pointer-events:
            none` and paints grey press feedback on the nearest <a>,
            which used to be THIS logo Link even when the user tapped
            the hamburger button. Killing the highlight here (and on the
            overlay wrapper) makes feedback land on the real hit target. */}
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none xl:static xl:inset-auto xl:justify-self-center xl:pointer-events-auto"
          style={{ WebkitTapHighlightColor: "transparent" }}
        >
          <Link
            to="/"
            className="flex items-center gap-2 pointer-events-auto"
            style={{ WebkitTapHighlightColor: "transparent" }}
            onClick={() => {
              // A logo tap from anywhere must reset feed source AND
              // close every open modal / sheet / overlay so the user
              // lands at a clean home state regardless of context.
              window.dispatchEvent(new CustomEvent("globaleyes:reset-feed-source"));
              window.dispatchEvent(new CustomEvent("globaleyes:close-overlays"));
              setMobileMenuOpen(false);
              setShowAuth(false);
              // Always return to the TOP of the home page — if the user
              // navigates from mid-page on /map, /commissions, etc., the
              // browser preserves scroll on a same-document SPA push,
              // which creates a confusing "home but not at the top"
              // experience. Force the reset on every logo tap.
              try {
                window.scrollTo({ top: 0, left: 0, behavior: "instant" });
              } catch (_e) {
                // Older Safari (<15.4) lacks ScrollBehavior:'instant' —
                // fall back to a plain numeric call which is always
                // synchronous.
                window.scrollTo(0, 0);
              }
            }}
            data-testid="logo-home-link"
          >
            <Globe className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-500" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              GlobalEyes.TV
            </span>
          </Link>
        </div>

        {/* Right: Desktop User Section — column 3 of the xl grid;
            justify-self-end keeps it pinned to the right edge without
            needing absolute positioning. */}
        <div className="hidden xl:flex items-center gap-3 justify-self-end min-w-0">
          <HeaderDesktopUserMenu
            user={user}
            logout={logout}
            newFollowers={newFollowers}
            inboxUnread={inboxUnread}
            expiredAlertsUnread={expiredAlertsUnread}
            creditsBlink={creditsBlink}
            onOpenAuth={openAuthDialog}
          />
        </div>

        {/* Mobile Menu Button + Sheet */}
        <div className="flex xl:hidden items-center absolute right-4">
          <HeaderMobileSheet
            user={user}
            logout={logout}
            newFollowers={newFollowers}
            inboxUnread={inboxUnread}
            expiredAlertsUnread={expiredAlertsUnread}
            open={mobileMenuOpen}
            onOpenChange={setMobileMenuOpen}
            onOpenAuth={openAuthDialog}
          />
        </div>
      </div>

      {/* Shared Auth Dialog. iOS keyboard fix: default shadcn Dialog
          centers itself with `top-1/2 translate-y-[-50%]`, which on iOS
          WKWebView fights the visualViewport when the soft keyboard
          appears. We override to:
            - anchor near the top on mobile (top-4) so the keyboard
              pushes content normally without flexing the dialog,
            - cap height at 92dvh and enable smooth touch scrolling
              so the user can swipe through inputs while typing,
            - revert to centered on sm+ where there's no soft keyboard. */}
      {!user && (
        <Dialog open={showAuth} onOpenChange={setShowAuth}>
          <DialogContent
            className="bg-gray-900 border-gray-800 text-white max-w-[95vw] sm:max-w-md
                       top-4 translate-y-0 sm:top-1/2 sm:translate-y-[-50%]
                       max-h-[92dvh] overflow-y-auto"
            style={{ WebkitOverflowScrolling: "touch" }}
            // Stop Radix Dialog's default behavior of auto-focusing the
            // first focusable child (the email <Input>) — on iOS that
            // immediately pops the keyboard and covers half the form,
            // before the user has even read the title.
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle className="text-xl sm:text-2xl font-bold text-center">
                {isLogin ? "Welcome Back!" : "Join GlobalEyes"}
              </DialogTitle>
              <DialogDescription className="text-center text-gray-400 text-sm">
                {isLogin
                  ? "Sign in to your account"
                  : "Get 20 free credits to start, plus instant access to the feed."}
              </DialogDescription>
            </DialogHeader>
            <AuthForm isLogin={isLogin} onSuccess={() => setShowAuth(false)} />
            <div className="text-center text-sm text-gray-400">
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-emerald-400 hover:underline"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? "Sign up" : "Sign in"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </header>
  );
};

export default Header;
